import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from './useDebounce';
import { queryKeys } from '@/lib/query-keys';

export type SearchEntityType =
  | 'client'
  | 'meeting'
  | 'project'
  | 'contract'
  | 'obligation'
  | 'script'
  | 'team_member'
  | 'client_team'
  | 'task'
  | 'synced_task'  // ClickUp synced tasks - links to Task Detail page
  | 'goal';

export interface SearchResult {
  id: string;
  entity_type: SearchEntityType;
  title: string;
  subtitle: string | null;
  client_id: string | null;
  client_name: string | null;
  relevance: number;
  url: string;
  created_at: string;
  content_preview?: string | null;
}

export interface UseUniversalSearchOptions {
  clientId?: string | null;
  enabled?: boolean;
  limit?: number;
  useVectorSearch?: boolean;
}

/**
 * Generate query embedding via edge function
 */
async function getQueryEmbedding(query: string): Promise<number[] | null> {
  try {
    const { data, error } = await supabase.functions.invoke('generate-embedding', {
      body: {
        text: query,
        isQuery: true,
      },
    });

    if (error) {
      console.error('Error generating query embedding:', error);
      return null;
    }

    return data?.embedding || null;
  } catch (e) {
    console.error('Failed to generate query embedding:', e);
    return null;
  }
}

/**
 * Hook for universal search across all entities
 * Uses hybrid search: FTS for text-based entities + vector search for meetings/contracts
 */
export function useUniversalSearch(
  query: string,
  options: UseUniversalSearchOptions = {}
) {
  const { clientId = null, enabled = true, limit = 20, useVectorSearch = false } = options; // Vector search disabled - pgvector not fully configured
  const debouncedQuery = useDebounce(query, 300);

  return useQuery({
    queryKey: queryKeys.search.universal(debouncedQuery, clientId, limit, useVectorSearch),
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.trim().length < 2) {
        return [];
      }

      const searchText = debouncedQuery.trim();

      // Try to generate query embedding for vector search
      let queryEmbedding: number[] | null = null;
      if (useVectorSearch) {
        queryEmbedding = await getQueryEmbedding(searchText);
      }

      // If we have an embedding, use hybrid search
      if (queryEmbedding && queryEmbedding.length > 0) {
        // Format embedding as PostgreSQL vector string
        const embeddingString = `[${queryEmbedding.join(',')}]`;

        const { data, error } = await supabase.rpc('hybrid_search', {
          search_query: searchText,
          query_embedding: embeddingString,
          p_client_id: clientId || null,
          p_limit: limit,
        });

        if (error) {
          console.error('Hybrid search error:', error);
          // Fall back to FTS-only search
          return fallbackToFTSSearch(searchText, clientId, limit);
        }

        return (data || []) as SearchResult[];
      }

      // Fall back to FTS-only search
      return fallbackToFTSSearch(searchText, clientId, limit);
    },
    enabled: enabled && debouncedQuery.trim().length >= 2,
    staleTime: 30000, // Cache results for 30 seconds
    gcTime: 60000, // Keep in cache for 1 minute
  });
}

/**
 * Fallback to FTS-only search when vector search is unavailable
 */
async function fallbackToFTSSearch(
  searchText: string,
  clientId: string | null,
  limit: number
): Promise<SearchResult[]> {
  const { data, error } = await supabase.rpc('universal_search', {
    search_query: searchText,
    p_client_id: clientId || null,
    p_limit: limit,
  });

  if (error) {
    console.error('FTS search error:', error);
    throw error;
  }

  return (data || []) as SearchResult[];
}
